import Download from "lucide-react/dist/esm/icons/download";
import MonitorUp from "lucide-react/dist/esm/icons/monitor-up";
import X from "lucide-react/dist/esm/icons/x";
import { useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

const DISMISSED_KEY = "needle-drop-install-prompt-dismissed";

function isInstalled() {
  return window.matchMedia("(display-mode: standalone), (display-mode: fullscreen)").matches
    || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
}

export function InstallPrompt() {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent>();
  const [visible, setVisible] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (isInstalled() || sessionStorage.getItem(DISMISSED_KEY)) return;

    const fallbackTimer = window.setTimeout(() => setVisible(true), 900);

    const onInstallAvailable = (event: Event) => {
      event.preventDefault();
      setInstallEvent(event as BeforeInstallPromptEvent);
      setVisible(true);
    };
    const onInstalled = () => {
      setInstallEvent(undefined);
      setVisible(false);
    };

    window.addEventListener("beforeinstallprompt", onInstallAvailable);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.clearTimeout(fallbackTimer);
      window.removeEventListener("beforeinstallprompt", onInstallAvailable);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!visible) return null;

  async function install() {
    if (!installEvent) {
      setShowHelp(true);
      return;
    }
    try {
      await installEvent.prompt();
      await installEvent.userChoice;
    } catch (error) {
      console.warn("Needle Drop could not open the installation prompt.", error);
    } finally {
      setInstallEvent(undefined);
      setVisible(false);
    }
  }

  function dismiss() {
    sessionStorage.setItem(DISMISSED_KEY, "1");
    setInstallEvent(undefined);
    setVisible(false);
  }

  return <aside className="install-prompt" aria-live="polite" aria-label="Install Needle Drop">
    <div className="install-prompt-title">
      <span><MonitorUp size={12} aria-hidden="true" /> DESKTOP MODE AVAILABLE</span>
      <button type="button" aria-label="Dismiss installation message" onClick={dismiss}><X size={12} /></button>
    </div>
    <div className="install-prompt-body">
      <div className="install-prompt-mark" aria-hidden="true">ND</div>
      <div>
        <strong>INSTALL NEEDLE DROP</strong>
        <p>{showHelp
          ? "Chrome/Edge: open the browser menu and choose Install page as app. Safari: File → Add to Dock."
          : "Open the private cloud library fullscreen without browser controls."}</p>
      </div>
    </div>
    <div className="install-prompt-actions">
      <button type="button" className="bevel-button" onClick={dismiss}>LATER</button>
      <button type="button" className="bevel-button primary-tool" onClick={() => void install()}><Download size={12} /> {installEvent ? "INSTALL" : "INSTALL HELP"}</button>
    </div>
  </aside>;
}
